/**
 * EmployeeNode — kafelek pracownika w organigramie (React Flow custom node).
 *
 * F24 Pakiet 0 (rebase organigramu na drzewo jednostek organizacyjnych): pracownik
 * jest teraz zawsze LIŚCIEM drzewa, zagnieżdżonym pod węzłem swojej jednostki
 * (`OrgUnitNode.tsx`) — sam nie ma już podwładnych na tym wykresie (dawny model po
 * płaskim `managerEmployeeId` dawał drzewo pracownik→pracownik; dziś przełożony
 * wynika z kierownika jednostki, z opcjonalnym nadpisaniem widocznym na karcie
 * pracownika, nie na tym diagramie — patrz `OrgChartCanvas.tsx`). Stąd usunięte pola
 * `department`/`isRoot` (kontekst „jednostka” niesie już samo zagnieżdżenie) i
 * handle źródłowy na dole (liść, nic się pod nim nie łączy).
 *
 * Handle: target góra (jednostka, do której należy).
 */

import { memo } from 'react';
import { Handle, type NodeProps, Position } from '@xyflow/react';

export interface EmployeeNodeData {
  name: string;
  /** Etykieta stanowiska (rozwiązana ze słownika `employee_positions`) albo `null`. */
  position: string | null;
  initials: string;
  [key: string]: unknown;
}

export const EmployeeNode = memo(function EmployeeNode({ data }: NodeProps) {
  const d = (data as EmployeeNodeData) ?? ({} as EmployeeNodeData);

  return (
    <div
      className="w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800"
      data-testid="org-node-employee"
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2 !border-white !bg-gray-400 dark:!border-gray-800"
      />
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-accent-500 text-sm font-semibold text-white"
          aria-hidden="true"
        >
          {d.initials}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {d.name}
          </div>
          {d.position && (
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">{d.position}</div>
          )}
        </div>
      </div>
    </div>
  );
});
